import { useEffect, useMemo, useState } from "react";
import { getClients } from "../api/clients";
import CreateClientModal from "../components/clients/CreateClientModal";
import { Link, useSearchParams } from "react-router-dom";
import "../styles/pages/clients-page.css";

type Filter = "all" | "account" | "orders";

export default function ClientsPage() {
  const [clients, setClients] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [params, setParams] = useSearchParams();

  const filter: Filter = params.get("filter") === "account" ? "account" : params.get("filter") === "orders" ? "orders" : "all";
  const setFilter = (f: Filter) => setParams(f === "all" ? {} : { filter: f }, { replace: true });

  const loadClients = async () => {
    const res = await getClients();
    setClients(res.data);
  };

  useEffect(() => { loadClients(); }, []);

  const counts = useMemo(() => ({
    all: clients.length,
    account: clients.filter((c) => c.siteAccount).length,
    orders: clients.filter((c) => (c.siteOrders ?? 0) > 0).length,
  }), [clients]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return clients.filter((c) => {
      if (filter === "account" && !c.siteAccount) return false;
      if (filter === "orders" && !((c.siteOrders ?? 0) > 0)) return false;
      return `${c.firstName} ${c.lastName} ${c.phone} ${c.email} ${c.companyName}`.toLowerCase().includes(s);
    });
  }, [search, clients, filter]);

  const chip = (id: Filter, label: string) => (
    <button
      key={id}
      onClick={() => setFilter(id)}
      style={{
        width: "auto", padding: "5px 12px", borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: "pointer",
        border: "1px solid " + (filter === id ? "#6366f1" : "#374151"),
        background: filter === id ? "rgba(99,102,241,0.2)" : "transparent",
        color: filter === id ? "#c7d2fe" : "#9ca3af", whiteSpace: "nowrap",
      }}
    >
      {label} <span style={{ opacity: 0.7 }}>{counts[id]}</span>
    </button>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "#0a0d14", overflow: "hidden" }}>

      {/* Topbar */}
      <div className="cl-topbar" style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "10px 20px", background: "#0f1117",
        borderBottom: "1px solid #1f2937", flexShrink: 0, gap: 12, flexWrap: "wrap",
      }}>
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#f9fafb" }}>Клиенты</h2>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {chip("all", "Все")}
          {chip("account", "Аккаунт на сайте")}
          {chip("orders", "Покупали на сайте")}
        </div>
        <div className="cl-controls" style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            className="cl-search"
            type="text"
            placeholder="Поиск клиента..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              background: "#1a2030", border: "1px solid #374151", borderRadius: 7,
              color: "#f9fafb", fontSize: 13, padding: "6px 12px", outline: "none",
              width: 220,
            }}
          />
          <button
            className="cl-create"
            onClick={() => setShowModal(true)}
            style={{
              background: "#6366f1", color: "#fff", border: "none", borderRadius: 8,
              padding: "7px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
            }}
          >
            + Создать клиента
          </button>
        </div>
      </div>

      {/* Grid */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          gap: 10,
        }}>
          {filtered.length ? filtered.map((c) => {
            const initials = (c.firstName?.[0] || "") + (c.lastName?.[0] || "");
            return (
              <Link
                to={`/clients/${c.id}`}
                key={c.id}
                style={{
                  display: "flex", gap: 10, padding: "10px 12px",
                  borderRadius: 10, background: "#131926", border: "1px solid #1f2937",
                  textDecoration: "none", color: "inherit", alignItems: "center",
                  transition: "border-color 0.15s, background 0.15s",
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "#1a2030"; (e.currentTarget as HTMLElement).style.borderColor = "#374151"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "#131926"; (e.currentTarget as HTMLElement).style.borderColor = "#1f2937"; }}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: "50%", background: "#6366f1",
                  color: "#fff", fontSize: 13, fontWeight: 700, flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {initials}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#f9fafb", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {c.firstName} {c.lastName}
                  </div>
                  <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{c.companyName || "Физ-лицо"}</div>
                  <div style={{ fontSize: 11, color: "#9ca3af" }}>{c.phone || "—"}</div>
                  {(c.siteAccount || (c.siteOrders ?? 0) > 0) && (
                    <div style={{ marginTop: 4, display: "flex", gap: 6, fontSize: 10 }}>
                      {c.siteAccount && <span style={{ color: "#a5b4fc", border: "1px solid #4338ca", borderRadius: 6, padding: "0 6px" }}>🌐 аккаунт</span>}
                      {(c.siteOrders ?? 0) > 0 && <span style={{ color: "#6ee7b7", border: "1px solid #047857", borderRadius: 6, padding: "0 6px" }}>🛒 {c.siteOrders}</span>}
                    </div>
                  )}
                </div>
              </Link>
            );
          }) : (
            <div style={{ gridColumn: "1/-1", padding: "40px 0", textAlign: "center", color: "#6b7280", fontSize: 13 }}>
              Клиентов не найдено
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <CreateClientModal onClose={() => setShowModal(false)} onCreated={loadClients} />
      )}
    </div>
  );
}
